import { useState, useEffect, useCallback } from 'react'
import { useTheme, ThemeType } from '../contexts/ThemeContext'
import { playPreview } from '../utils/sounds'

interface GraphMember {
  displayName: string
  mail: string | null
  userPrincipalName: string
}

interface SettingsProps {
  isOpen: boolean
  onClose: () => void
  projectName?: string
  getAuthHeaders?: () => Record<string, string>
  graphToken?: string | null
  graphTokenError?: string | null
  onSignOut?: () => void
  /** Called after a project is archived, so the parent can refresh the list. */
  onProjectArchived?: () => void
}

const NOTIFICATION_SOUNDS = [
  { id: 'chime', label: 'Chime', description: 'Sci-fi UI beep', corporateDescription: 'Short alert tone' },
  { id: 'bell', label: 'Bell', description: 'Starship notification', corporateDescription: 'Soft notification chime' },
  { id: 'subtle', label: 'Subtle', description: 'Quiet radar ping', corporateDescription: 'Quiet background ping' },
  { id: 'none', label: 'None', description: 'Silent', corporateDescription: 'Silent' },
]

export function Settings({ isOpen, onClose, projectName, getAuthHeaders, graphToken, graphTokenError, onSignOut, onProjectArchived }: SettingsProps) {
  if (!isOpen) return null

  return <SettingsContent onClose={onClose} projectName={projectName} getAuthHeaders={getAuthHeaders} graphToken={graphToken} graphTokenError={graphTokenError} onSignOut={onSignOut} onProjectArchived={onProjectArchived} />
}

function SettingsContent({ onClose, projectName, getAuthHeaders, graphToken, graphTokenError, onSignOut, onProjectArchived }: { onClose: () => void; projectName?: string; getAuthHeaders?: () => Record<string, string>; graphToken?: string | null; graphTokenError?: string | null; onSignOut?: () => void; onProjectArchived?: () => void }) {
  const { theme, setTheme, settings, updateSettings } = useTheme()
  const [members, setMembers] = useState<string[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersSaving, setMembersSaving] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [membersDirty, setMembersDirty] = useState(false)
  const [groupMembers, setGroupMembers] = useState<GraphMember[]>([])
  const [graphLoaded, setGraphLoaded] = useState(false)
  const [graphLoading, setGraphLoading] = useState(false)
  const [graphError, setGraphError] = useState<string | null>(null)
  const [selectedGroupMember, setSelectedGroupMember] = useState('')

  // Fetch current members from backend
  const fetchMembers = useCallback(async () => {
    if (!projectName || !getAuthHeaders) return
    setMembersLoading(true)
    setMembersError(null)
    try {
      const headers = getAuthHeaders()
      const response = await fetch(`/api/projects/${encodeURIComponent(projectName)}/members`, { headers })
      if (response.ok) {
        const data = await response.json()
        setMembers(data.members || [])
      }
    } catch {
      setMembersError('Failed to fetch members')
    } finally {
      setMembersLoading(false)
    }
  }, [projectName, getAuthHeaders])

  // Fetch group members from Graph API using the token from App
  const fetchGroupMembers = useCallback(async () => {
    const groupId = import.meta.env.VITE_SECURITY_GROUP_ID
    if (!groupId || !graphToken) return

    setGraphLoading(true)
    setGraphError(null)

    try {
      const response = await fetch(
        `https://graph.microsoft.com/v1.0/groups/${groupId}/members`,
        { headers: { Authorization: `Bearer ${graphToken}` } }
      )

      if (!response.ok) {
        const body = await response.text().catch(() => '')
        throw new Error(`Graph API ${response.status}: ${body.slice(0, 200)}`)
      }

      const data = await response.json()
      const result: GraphMember[] = data.value || []
      setGroupMembers(result)
      setGraphLoaded(true)
      if (result.length === 0) {
        setGraphError('Security group has no members')
      }
    } catch (err) {
      console.error('Graph API call failed:', err)
      setGraphError(err instanceof Error ? err.message : 'Failed to load directory')
    } finally {
      setGraphLoading(false)
    }
  }, [graphToken])

  useEffect(() => {
    fetchMembers()
    fetchGroupMembers()
  }, [fetchMembers, fetchGroupMembers])

  const handleAddMember = (email: string) => {
    if (!email.trim()) return
    const normalized = email.trim().toLowerCase()
    if (members.some(m => m.toLowerCase() === normalized)) return
    setMembers(prev => [...prev, email.trim()])
    setMembersDirty(true)
    setSelectedGroupMember('')
  }

  const handleRemoveMember = (email: string) => {
    setMembers(prev => prev.filter(m => m !== email))
    setMembersDirty(true)
  }

  const handleSaveMembers = async () => {
    if (!projectName || !getAuthHeaders) return
    setMembersSaving(true)
    setMembersError(null)
    try {
      const headers = getAuthHeaders()
      const response = await fetch(`/api/projects/${encodeURIComponent(projectName)}/members`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ members }),
      })
      const data = await response.json()
      if (data.success) {
        setMembersDirty(false)
      } else {
        setMembersError(data.error || 'Failed to update members')
      }
    } catch {
      setMembersError('Failed to save members')
    } finally {
      setMembersSaving(false)
    }
  }

  const availableGroupMembers = groupMembers.filter(
    gm => !members.some(m => m.toLowerCase() === (gm.mail || gm.userPrincipalName).toLowerCase())
  )

  const handleThemeChange = (newTheme: ThemeType) => {
    setTheme(newTheme)
  }

  const handleSoundChange = (sound: string) => {
    updateSettings({ notificationSound: sound })
    playPreview(sound)
  }

  const [isDownloading, setIsDownloading] = useState(false)

  // Archive: a two-step (confirm) destructive-ish action. Archiving the repo on
  // GitHub drops the project out of discovery (github.rs::discover skips
  // archived repos), removing it from the list. Reversible by un-archiving.
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)

  const handleArchive = useCallback(async () => {
    if (!projectName || !getAuthHeaders) return
    setIsArchiving(true)
    setArchiveError(null)
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(projectName)}/archive`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
      const data = await response.json()
      if (data.success) {
        onProjectArchived?.()
        onClose()
      } else {
        setArchiveError(data.error || 'Failed to archive project')
      }
    } catch {
      setArchiveError('Failed to archive project')
    } finally {
      setIsArchiving(false)
      setConfirmArchive(false)
    }
  }, [projectName, getAuthHeaders, onProjectArchived, onClose])

  const handleDownload = useCallback(async () => {
    if (!projectName || !getAuthHeaders) return
    setIsDownloading(true)
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(projectName)}/download`, {
        headers: getAuthHeaders(),
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${projectName}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download project:', error)
    } finally {
      setIsDownloading(false)
    }
  }, [projectName, getAuthHeaders])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md m-4 themed-panel max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-cosmic-star via-cosmic-nebula to-cosmic-plasma bg-clip-text text-transparent">
            Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close settings"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Visual Style */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Visual Style</h3>
            <div className="space-y-2">
              <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-white/5 transition-colors">
                <input
                  type="radio"
                  name="theme"
                  value="glass-morphism"
                  checked={theme === 'glass-morphism'}
                  onChange={() => handleThemeChange('glass-morphism')}
                  className="w-4 h-4 text-cosmic-nebula"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Glass morphism</div>
                  <div className="text-sm text-gray-400">Frosted panels with backdrop blur</div>
                </div>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-white/5 transition-colors">
                <input
                  type="radio"
                  name="theme"
                  value="soft-shadows"
                  checked={theme === 'soft-shadows'}
                  onChange={() => handleThemeChange('soft-shadows')}
                  className="w-4 h-4 text-cosmic-nebula"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Soft shadows</div>
                  <div className="text-sm text-gray-400">Conventional card shadows</div>
                </div>
              </label>
              <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-white/5 transition-colors">
                <input
                  type="radio"
                  name="theme"
                  value="corporate"
                  checked={theme === 'corporate'}
                  onChange={() => handleThemeChange('corporate')}
                  className="w-4 h-4 text-cosmic-nebula"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Corporate</div>
                  <div className="text-sm text-gray-400">Clean light theme for enterprise</div>
                </div>
              </label>
            </div>
          </div>

          {/* Notification Sound */}
          <div>
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Notification Sound</h3>
            <div className="space-y-2">
              {NOTIFICATION_SOUNDS.map((sound) => (
                <label
                  key={sound.id}
                  className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <input
                    type="radio"
                    name="sound"
                    value={sound.id}
                    checked={settings.notificationSound === sound.id}
                    onChange={() => handleSoundChange(sound.id)}
                    className="w-4 h-4 text-cosmic-nebula"
                  />
                  <div className="flex-1">
                    <div className="text-white font-medium">{sound.label}</div>
                    <div className="text-sm text-gray-400">{theme === 'corporate' ? sound.corporateDescription : sound.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Project Members */}
          {projectName && getAuthHeaders && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Project Members
                <span className="text-gray-500 font-normal ml-2">— {projectName}</span>
              </h3>

              {membersError && (
                <div className="p-2 mb-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                  {membersError}
                </div>
              )}

              {membersLoading ? (
                <div className="text-gray-500 text-sm">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {/* Current members */}
                  {members.length === 0 ? (
                    <p className="text-gray-500 text-sm italic">
                      No members configured. Only the default owner has access.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {members.map(email => (
                        <div key={email} className="flex items-center justify-between py-1.5 px-3 bg-white/5 rounded-lg">
                          <span className="text-sm text-gray-200">{email}</span>
                          <button
                            onClick={() => handleRemoveMember(email)}
                            className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                            title="Remove member"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add member dropdown */}
                  {graphLoading ? (
                    <div className="px-3 py-2 text-sm text-gray-500 bg-white/5 border border-white/10 rounded-lg">
                      Loading directory...
                    </div>
                  ) : graphError ? (
                    <div className="px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg">
                      {graphError}
                    </div>
                  ) : availableGroupMembers.length > 0 ? (
                    <div className="flex gap-2">
                      <select
                        value={selectedGroupMember}
                        onChange={(e) => setSelectedGroupMember(e.target.value)}
                        className="flex-1 appearance-none bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cosmic-nebula"
                      >
                        <option value="">Add a member...</option>
                        {availableGroupMembers.map(gm => {
                          const email = gm.mail || gm.userPrincipalName
                          return (
                            <option key={email} value={email}>
                              {gm.displayName}
                            </option>
                          )
                        })}
                      </select>
                      <button
                        onClick={() => handleAddMember(selectedGroupMember)}
                        disabled={!selectedGroupMember}
                        className="px-3 py-2 text-sm font-medium text-white bg-cosmic-nebula hover:bg-cosmic-nebula/80 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  ) : graphLoaded ? (
                    <p className="text-gray-500 text-sm italic">All directory members already added</p>
                  ) : !graphToken && graphTokenError ? (
                    <div className="px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg">
                      Directory error: {graphTokenError}
                    </div>
                  ) : !graphToken ? (
                    <div className="px-3 py-2 text-sm text-gray-500 bg-white/5 border border-white/10 rounded-lg">
                      Loading directory...
                    </div>
                  ) : null}

                  {/* Save button */}
                  {membersDirty && (
                    <button
                      onClick={handleSaveMembers}
                      disabled={membersSaving}
                      className="w-full px-4 py-2 text-sm font-medium text-white bg-cosmic-nebula hover:bg-cosmic-nebula/80 disabled:opacity-50 rounded-lg transition-colors"
                    >
                      {membersSaving ? 'Saving...' : 'Save Members'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Download Code */}
          {projectName && getAuthHeaders && (
            <div>
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Download Code</h3>
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full px-4 py-2 text-sm font-medium text-white bg-cosmic-nebula hover:bg-cosmic-nebula/80 disabled:opacity-50 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isDownloading ? 'Downloading...' : 'Download as ZIP'}
              </button>
              <p className="text-xs text-gray-500 mt-2">Download the main branch of the project repository.</p>
            </div>
          )}

          {/* Archive Project */}
          {projectName && getAuthHeaders && (
            <div className="pt-2 border-t border-white/10">
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Archive Project</h3>

              {archiveError && (
                <div className="p-2 mb-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                  {archiveError}
                </div>
              )}

              {confirmArchive ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-400">
                    Remove <span className="text-gray-200 font-medium">{projectName}</span> from the project
                    list? This archives the GitHub repo — reversible by un-archiving it on GitHub.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleArchive}
                      disabled={isArchiving}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 disabled:opacity-50 rounded-lg transition-colors"
                    >
                      {isArchiving ? 'Archiving...' : 'Confirm archive'}
                    </button>
                    <button
                      onClick={() => setConfirmArchive(false)}
                      disabled={isArchiving}
                      className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setConfirmArchive(true)}
                    className="w-full px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30 rounded-lg transition-colors"
                  >
                    Archive project
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Stops the workflow and removes the project from the list. The repo is kept (archived) and can be restored.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Sign Out */}
          {onSignOut && (
            <div className="pt-2 border-t border-white/10">
              <button
                onClick={onSignOut}
                className="w-full px-4 py-2 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
